import React, { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import api from '../api/axiosConfig';
import { useModal } from '../contexts/ModalContext';

const ROLES = [
    { id: 'STAFF', name: '일반 작업자', desc: '점검하고 이슈를 올립니다' },
    { id: 'INTERMEDIATE_ADMIN', name: '중간 관리자', desc: '맡긴 것만 관리합니다' },
    { id: 'SUPER_ADMIN', name: '최고 관리자', desc: '모든 기능' },
    { id: 'CLIENT', name: '건물주', desc: '자기 건물만 봅니다' },
];

/**
 * 초대를 만들고 링크를 건네는 자리.
 *
 * 예전에는 여기서 계정을 바로 만들었고 비밀번호가 `0000` 이었다. 아는 사람끼리
 * 쓸 때는 괜찮았지만, 이메일만 알면 본인보다 먼저 로그인할 수 있는 계정이다.
 *
 * 만들고 나면 링크는 부모가 공용 모달로 보여준다. 여기서도 그리면 '다시 볼 수
 * 없다' 는 경고가 한쪽에만 남는 날이 온다.
 */
const InviteModal = ({ show, onClose, onSuccess }) => {
    const { showAlert } = useModal();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('STAFF');
    const [clientId, setClientId] = useState('');
    const [reportIssue, setReportIssue] = useState(false);
    const [clients, setClients] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!show) return;
        setName('');
        setEmail('');
        setRole('STAFF');
        setClientId('');
        setReportIssue(false);
        api
            .get('/clients')
            .then((res) => setClients(res.data || []))
            .catch((err) => console.error('Failed to load clients', err));
    }, [show]);

    const create = async () => {
        if (!name.trim() || !email.trim()) {
            showAlert('알림', '이름과 이메일을 적어 주세요.', 'warning');
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post('/invitations', {
                name,
                email,
                role,
                clientId: role === 'CLIENT' ? Number(clientId) || null : null,
                permissions:
                    role === 'CLIENT' ? { client_report_issue: reportIssue } : null,
            });
            // 링크는 부모가 공용 모달로 보여준다. 두 곳에서 그리면 '다시 볼 수
            // 없다' 는 경고가 한쪽에만 남는 날이 온다.
            onSuccess?.({
                name: name.trim(),
                url: `${window.location.origin}/invite/${data.token}`,
            });
            onClose();
        } catch (err) {
            showAlert('오류', err.response?.data?.message || '만들지 못했습니다.', 'danger');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title className="fw-bold">사용자 초대</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={(e) => { e.preventDefault(); create(); }}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">이름 <span className="text-danger">*</span></Form.Label>
                            <Form.Control value={name} onChange={(e) => setName(e.target.value)} placeholder="홍길동" />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">이메일 <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="name@example.com"
                            />
                            <Form.Text className="text-muted">이 주소로 로그인합니다.</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">역할</Form.Label>
                            <Form.Select value={role} onChange={(e) => setRole(e.target.value)}>
                                {ROLES.map((r) => (
                                    <option key={r.id} value={r.id}>{r.name} — {r.desc}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {role === 'CLIENT' && (
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">어느 건물주</Form.Label>
                                <Form.Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
                                    <option value="">고르세요</option>
                                    {clients.map((c) => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    {clients.length === 0
                                        ? '등록된 건물주가 없습니다. 시설 › 건물주에서 먼저 더해 주세요.'
                                        : '그 건물주의 건물만 봅니다.'}
                                </Form.Text>
                                <Form.Check
                                    className="mt-2"
                                    type="switch"
                                    id="invite-report-issue"
                                    label="이슈 등록 허용"
                                    checked={reportIssue}
                                    onChange={() => setReportIssue(!reportIssue)}
                                />
                            </Form.Group>
                        )}

                        {role === 'INTERMEDIATE_ADMIN' && (
                            <div className="text-muted mb-3" style={{ fontSize: '0.78rem' }}>
                                무엇을 맡길지는 가입한 뒤 권한 설정에서 고릅니다.
                            </div>
                        )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onClose}>취소</Button>
                <Button variant="primary" onClick={create} disabled={saving}>
                    {saving ? '만드는 중…' : '초대 만들기'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default InviteModal;
